<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen py-10 px-4">
    <AppHeader />

    <div class="max-w-5xl mx-auto space-y-6">
      <h1 class="text-3xl font-bold">Minhas Propostas</h1>

      <!-- LISTA DE PROPOSTAS -->
      <div v-for="proposal in proposals" :key="proposal.id" class="bg-white shadow rounded-2xl p-6">
        <div class="flex justify-between items-start">
          <div>
            <router-link :to="{
              name: 'detalhes',
              params: { hash: proposal.project.hash, slug: proposal.project.slug }
            }" class="text-xl font-semibold text-[#1f2937] underline">
              {{ proposal.project.title }}
            </router-link>
            <p class="text-gray-500 text-sm">
              Enviada em: {{ formatDate(proposal.created_at) }}
            </p>
          </div>

          <strong class="text-lg">
            R$ {{ formatMoney(proposal.total_amount) }}
          </strong>
        </div>

        <p class="mt-2 text-gray-700">
          <strong>Prazo estimado:</strong> {{ proposal.estimated_days }} dias
        </p>

        <button @click="openMessagesModal(proposal)"
          class="mt-4 bg-[#b99a52] text-white px-4 py-2 rounded-xl font-semibold mr-2">
          Ver Conversa
        </button>
        <span v-if="unreadCount(proposal) > 0"
          class="inline-block mt-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
          {{ unreadCount(proposal) === 1 ? '1 nova mensagem' : unreadCount(proposal) + ' novas mensagens' }}
        </span>
      </div>
    </div>

    <!-- MODAL DE CONVERSA -->
    <div v-if="showMessagesModal"
      class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[3000]">
      <div class="bg-white w-full max-w-2xl p-6 rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto">
        <h2 class="text-xl font-bold mb-3">
          Conversa com {{ selectedProposal.project.user.name }}
        </h2>

        <div class="space-y-3">
          <div v-for="msg in selectedProposal.messages" :key="msg.id" class="p-3 rounded-xl"
            :class="msg.sender_id === userData.user.id ? 'bg-blue-100' : 'bg-gray-200'">
            <p class="text-sm text-gray-700 whitespace-pre-line">
              {{ msg.content }}
            </p>

            <!-- ANEXOS -->
            <div v-if="msg.attachments?.length" class="mt-2 space-y-1">
              <div v-for="(file, idx) in msg.attachments" :key="idx">
                <a :href="fileUrl(file)" target="_blank"
                  class="text-blue-600 underline text-sm flex items-center gap-1">
                  📎 {{ extractFileName(file) }}
                </a>
              </div>
            </div>

            <p class="text-xs text-gray-500 mt-1">
              {{ formatDate(msg.created_at) }}
            </p>
          </div>
        </div>

        <!-- ENVIO -->
        <div class="mt-4 flex flex-col gap-3">
          <div v-if="selectedFiles.length" class="bg-gray-100 p-2 rounded-xl border text-sm">
            <strong>Anexos selecionados:</strong>
            <ul class="list-disc ml-5 mt-1">
              <li v-for="(file, idx) in selectedFiles" :key="idx">
                {{ file.name }}
              </li>
            </ul>
          </div>

          <div class="flex gap-2 items-center">
            <textarea rows="3" v-model="newMessage" placeholder="Digite sua mensagem..." class="input-gold w-full" />

            <label class="cursor-pointer bg-gray-300 px-3 py-2 rounded-xl text-sm font-semibold">
              📎
              <input type="file" multiple class="hidden" @change="handleFileSelect" />
            </label>

            <button @click="sendMessage" class="bg-[#b99a52] text-white px-4 py-2 rounded-xl font-semibold">
              Enviar
            </button>
          </div>
        </div>

        <button @click="closeMessagesModal" class="mt-6 bg-red-600 text-white px-4 py-2 rounded-xl w-full">
          Fechar Conversa
        </button>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import api from '@/services/api'
import { useLoadingStore } from '@/store/loadingStore'

const { proxy } = getCurrentInstance()
const loadingStore = useLoadingStore()

const proposals = ref([])
const selectedProposal = ref(null)
const showMessagesModal = ref(false)
const newMessage = ref('')
const selectedFiles = ref([])

const userData = JSON.parse(localStorage.getItem('userData') || '{}')

onMounted(loadMyProposals)

async function loadMyProposals() {
  try {
    loadingStore.show()
    const res = await api.get(`/proposal/freelancer/${userData.user.id}`)
    proposals.value = res.data.data || []
  } catch (error) {
    proxy.$swal.fire({
      icon: 'error',
      text: error?.response?.data?.error || 'Erro ao carregar propostas'
    })
  } finally {
    loadingStore.hide()
  }
}

async function openMessagesModal(proposal) {
  selectedProposal.value = proposal
  showMessagesModal.value = true
  try {
    await loadConversation(proposal)

    try {
      await api.post('/messages/mark-read', {
        project_id: proposal.project_id,
        receiver_id: userData.user.id
      });

      await api.post('/proposal/mark-read', {
        proposal_id: proposal.id
      });

      proposal.is_read = 1;
      proposal.unread_messages = 0;
    } catch (errMark) {
      console.warn('Não foi possível marcar como lido:', errMark?.response?.data || errMark.message || errMark);
      proxy.$swal.fire({
        icon: "error",
        title: "",
        text: error?.response?.data?.error || "Não foi possível marcar como lido",
        customClass: {
          container: 'swal-top-layer'
        }
      })
    }
  } catch (error) {
    proxy.$swal.fire({
      icon: "error",
      title: "",
      text: error?.response?.data?.error || "Erro ao carregar as mensagens.",
      customClass: {
        container: 'swal-top-layer'
      }
    })
  } finally {
    loadingStore.hide();
  }
}

function closeMessagesModal() {
  showMessagesModal.value = false
}

async function loadConversation(proposal) {
  const res = await api.get('/messages/conversation', {
    params: {
      project_id: proposal.project_id,
      freelancer_id: userData.user.id
    }
  })
  selectedProposal.value.messages = res.data.data || []
}

async function sendMessage() {
  if (!newMessage.value && !selectedFiles.value.length) return

  const form = new FormData()
  form.append('project_id', selectedProposal.value.project_id)
  form.append('sender_id', userData.user.id)
  form.append('receiver_id', selectedProposal.value.project.user_id)
  form.append('content', newMessage.value)

  selectedFiles.value.forEach(f => form.append('attachments[]', f))

  await api.post('/messages/send', form)
  await loadConversation(selectedProposal.value)

  const payload = {
    project_id: selectedProposal.value.project_id,
    receiver_id: selectedProposal.value.project.user_id,
    content: newMessage.value
  }
  await api.post("/messages/send-mail-freelancer", payload);

  newMessage.value = ''
  selectedFiles.value = []
}

function unreadCount(p) {
  return Number(p.unread_messages) || 0
}

function handleFileSelect(e) {
  selectedFiles.value = Array.from(e.target.files)
}

function fileUrl(path) {
  return `${process.env.VUE_APP_URL_API_STORAGE}/${path.replace(/\\/g, '/')}`
}

function extractFileName(path) {
  return path.split(/[/\\]/).pop()
}

function formatDate(dt) {
  return new Date(dt).toLocaleDateString('pt-BR')
}

function formatMoney(v) {
  return Number(v).toFixed(2).replace('.', ',')
}
</script>

<style>
.swal-top-layer {
  z-index: 99999 !important;
}
</style>